import React, { useEffect, useRef, useState } from 'react';
import { Upload, Download, Pencil, Check } from 'lucide-react';

const CANVAS_SIZE = 1080;

interface Star {
  x: number;
  y: number;
  r: number;
  alpha: number;
  sparkle: boolean;
}

const makeStars = (count: number): Star[] =>
  Array.from({ length: count }, () => ({
    x: Math.random() * CANVAS_SIZE,
    y: Math.random() * CANVAS_SIZE,
    r: 0.6 + Math.random() * 1.8,
    alpha: 0.15 + Math.random() * 0.75,
    sparkle: Math.random() < 0.06,
  }));

const drawSparkle = (ctx: CanvasRenderingContext2D, x: number, y: number, size: number, alpha: number) => {
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.fillStyle = '#ffd702';
  ctx.beginPath();
  ctx.moveTo(x, y - size);
  ctx.quadraticCurveTo(x, y, x + size, y);
  ctx.quadraticCurveTo(x, y, x, y + size);
  ctx.quadraticCurveTo(x, y, x - size, y);
  ctx.quadraticCurveTo(x, y, x, y - size);
  ctx.fill();
  ctx.restore();
};

/** Reduce el tamaño de fuente hasta que el texto quepa en maxWidth. */
const fitFontSize = (
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
  startSize: number,
  minSize: number,
  weight = 800
) => {
  let size = startSize;
  while (size > minSize) {
    ctx.font = `${weight} ${size}px Arial, sans-serif`;
    if (ctx.measureText(text).width <= maxWidth) break;
    size -= 2;
  }
  return size;
};

export interface FinalistPreset {
  /** nomination_id: identifica de forma única a este finalista. */
  id: string;
  /** Texto mostrado en el desplegable "Selecciona quién eres". */
  label: string;
  /** Nombre o título que se escribe en la tarjeta. */
  name: string;
  /** Categoría que se escribe en la tarjeta. */
  category: string;
  /** Foto o logo ya disponible para rellenar la tarjeta automáticamente. */
  photoUrl?: string;
  /**
   * 'photo' (por defecto): retrato de persona, se recorta a círculo.
   * 'logo': logo institucional, rectangular, nunca se recorta a círculo,
   * se muestra completo sobre una placa blanca redondeada.
   */
  photoKind?: 'photo' | 'logo';
}

export interface FinalistShareCardProps {
  /** Texto de la pastilla superior, p.ej. "FINALISTA" o "ENTRE LOS 2 ÚLTIMOS". */
  pillText: string;
  /** Categorías disponibles en el desplegable (modo manual, sin presets). */
  categories: string[];
  /** Sufijo del nombre del archivo descargado, p.ej. "finalista" o "top2-finalista". */
  fileSuffix: string;
  /** Texto del placeholder cuando no se ha subido foto. */
  uploadHint?: string;
  /**
   * Si se indica, se muestra primero un selector "¿Quién eres?" que rellena
   * nombre, categoría y foto automáticamente a partir de datos ya conocidos,
   * con opción de editarlo todo a mano después.
   */
  presets?: FinalistPreset[];
}

const FinalistShareCard: React.FC<FinalistShareCardProps> = ({
  pillText,
  categories,
  fileSuffix,
  uploadHint = 'Sube tu foto',
  presets,
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const starsRef = useRef<Star[]>(makeStars(150));
  const logoImgRef = useRef<HTMLImageElement | null>(null);
  const photoImgRef = useRef<HTMLImageElement | null>(null);

  const [name, setName] = useState('');
  const [category, setCategory] = useState('');
  const [hasPhoto, setHasPhoto] = useState(false);
  const [logoReady, setLogoReady] = useState(false);
  const [error, setError] = useState('');
  const [selectedPresetId, setSelectedPresetId] = useState('');
  const [editing, setEditing] = useState(!presets || presets.length === 0);
  const [photoKind, setPhotoKind] = useState<'photo' | 'logo'>('photo');
  // Se incrementa cada vez que photoImgRef.current cambia. hasPhoto no sirve
  // para disparar el redibujado porque, al pasar de un finalista con foto a
  // otro, sigue valiendo `true` de principio a fin: React no ve cambio de
  // estado y el efecto de dibujo no se vuelve a ejecutar, dejando la imagen
  // anterior pintada un paso por detrás del nombre.
  const [photoVersion, setPhotoVersion] = useState(0);

  // Cargamos el logo una sola vez.
  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      logoImgRef.current = img;
      setLogoReady(true);
    };
    img.src = '/premios-logo.svg';
  }, []);

  const draw = React.useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;

    // Fondo — mismo degradado que el hero de la landing.
    const bg = ctx.createRadialGradient(
      CANVAS_SIZE / 2, 0, CANVAS_SIZE * 0.1,
      CANVAS_SIZE / 2, CANVAS_SIZE / 2, CANVAS_SIZE * 0.9
    );
    bg.addColorStop(0, '#003554');
    bg.addColorStop(0.55, '#002338');
    bg.addColorStop(1, '#00192a');
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);

    // Estrellas.
    starsRef.current.forEach((s) => {
      if (s.sparkle) {
        drawSparkle(ctx, s.x, s.y, s.r * 5, s.alpha);
      } else {
        ctx.save();
        ctx.globalAlpha = s.alpha;
        ctx.fillStyle = '#ffffff';
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      }
    });

    // Resplandor dorado central (igual que el glow del hero).
    const glow = ctx.createRadialGradient(
      CANVAS_SIZE / 2, CANVAS_SIZE * 0.42, 0,
      CANVAS_SIZE / 2, CANVAS_SIZE * 0.42, CANVAS_SIZE * 0.5
    );
    glow.addColorStop(0, 'rgba(255, 215, 2, 0.16)');
    glow.addColorStop(1, 'rgba(255, 215, 2, 0)');
    ctx.fillStyle = glow;
    ctx.fillRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);

    // Texto kicker superior.
    ctx.textAlign = 'center';
    ctx.fillStyle = 'rgba(230, 238, 246, 0.85)';
    ctx.font = '600 26px Arial, sans-serif';
    ctx.fillText('P R E M I O S   N O V A G O B   ·   E D I C I Ó N   2 0 2 6', CANVAS_SIZE / 2, 100);

    // Pastilla superior — el ancho se adapta al texto, para admitir mensajes
    // más largos que "FINALISTA" (p.ej. "ENTRE LOS 2 ÚLTIMOS") sin recortarse.
    const pillFontSize = 38;
    ctx.font = `800 ${pillFontSize}px Arial, sans-serif`;
    const pillTextWidth = ctx.measureText(pillText).width;
    const pillH = 76;
    const pillW = Math.min(CANVAS_SIZE - 120, Math.max(360, pillTextWidth + 100));
    const pillX = CANVAS_SIZE / 2 - pillW / 2;
    const pillY = 140;
    const pillGrad = ctx.createLinearGradient(pillX, pillY, pillX + pillW, pillY);
    pillGrad.addColorStop(0, '#e8ce00');
    pillGrad.addColorStop(1, '#ffb800');
    ctx.fillStyle = pillGrad;
    const r = pillH / 2;
    ctx.beginPath();
    ctx.moveTo(pillX + r, pillY);
    ctx.arcTo(pillX + pillW, pillY, pillX + pillW, pillY + pillH, r);
    ctx.arcTo(pillX + pillW, pillY + pillH, pillX, pillY + pillH, r);
    ctx.arcTo(pillX, pillY + pillH, pillX, pillY, r);
    ctx.arcTo(pillX, pillY, pillX + pillW, pillY, r);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = '#001322';
    ctx.font = `800 ${pillFontSize}px Arial, sans-serif`;
    ctx.textBaseline = 'middle';
    ctx.fillText(pillText, CANVAS_SIZE / 2, pillY + pillH / 2 + 2);
    ctx.textBaseline = 'alphabetic';

    const cx = CANVAS_SIZE / 2;
    const cy = 520;

    if (photoKind === 'logo') {
      // Logos institucionales: son rectangulares, nunca se recortan a
      // círculo (deformaría/cortaría el texto del logo). Se muestran
      // completos, en modo "contain", sobre una placa blanca redondeada.
      const boxW = 460;
      const boxH = 460;
      const boxX = cx - boxW / 2;
      const boxY = cy - boxH / 2;
      const boxR = 40;
      const roundedRectPath = () => {
        ctx.beginPath();
        ctx.moveTo(boxX + boxR, boxY);
        ctx.arcTo(boxX + boxW, boxY, boxX + boxW, boxY + boxH, boxR);
        ctx.arcTo(boxX + boxW, boxY + boxH, boxX, boxY + boxH, boxR);
        ctx.arcTo(boxX, boxY + boxH, boxX, boxY, boxR);
        ctx.arcTo(boxX, boxY, boxX + boxW, boxY, boxR);
        ctx.closePath();
      };

      ctx.save();
      roundedRectPath();
      ctx.fillStyle = '#ffffff';
      ctx.fill();

      if (photoImgRef.current) {
        ctx.save();
        roundedRectPath();
        ctx.clip();
        const img = photoImgRef.current;
        const pad = 40;
        const availW = boxW - pad * 2;
        const availH = boxH - pad * 2;
        const scale = Math.min(availW / img.width, availH / img.height);
        const w = img.width * scale;
        const h = img.height * scale;
        ctx.drawImage(img, cx - w / 2, cy - h / 2, w, h);
        ctx.restore();
      } else {
        ctx.fillStyle = 'rgba(0, 19, 34, 0.45)';
        ctx.font = '600 24px Arial, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(uploadHint, cx, cy + 8);
      }

      roundedRectPath();
      ctx.lineWidth = 10;
      ctx.strokeStyle = '#ffd702';
      ctx.stroke();
      ctx.restore();
    } else {
      // Foto de persona: círculo con anillo dorado, recorte "cover".
      const radius = 230;
      ctx.save();
      ctx.beginPath();
      ctx.arc(cx, cy, radius, 0, Math.PI * 2);
      ctx.closePath();
      ctx.fillStyle = 'rgba(255, 255, 255, 0.05)';
      ctx.fill();

      if (photoImgRef.current) {
        ctx.clip();
        const img = photoImgRef.current;
        const scale = Math.max((radius * 2) / img.width, (radius * 2) / img.height);
        const w = img.width * scale;
        const h = img.height * scale;
        ctx.drawImage(img, cx - w / 2, cy - h / 2, w, h);
      } else {
        ctx.clip();
        ctx.fillStyle = 'rgba(255, 255, 255, 0.03)';
        ctx.fillRect(cx - radius, cy - radius, radius * 2, radius * 2);
      }
      ctx.restore();

      if (!photoImgRef.current) {
        ctx.save();
        ctx.fillStyle = 'rgba(230, 238, 246, 0.5)';
        ctx.font = '600 24px Arial, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(uploadHint, cx, cy + 8);
        ctx.restore();
      }

      ctx.save();
      ctx.beginPath();
      ctx.arc(cx, cy, radius, 0, Math.PI * 2);
      ctx.lineWidth = 10;
      ctx.strokeStyle = '#ffd702';
      ctx.stroke();
      ctx.restore();
    }

    // Nombre.
    const displayName = name.trim() || 'Tu nombre aquí';
    const nameSize = fitFontSize(ctx, displayName, CANVAS_SIZE - 140, 62, 30);
    ctx.font = `800 ${nameSize}px Arial, sans-serif`;
    ctx.fillStyle = name.trim() ? '#ffffff' : 'rgba(230, 238, 246, 0.4)';
    ctx.textAlign = 'center';
    ctx.fillText(displayName, cx, 838);

    // Categoría (opcional).
    if (category.trim()) {
      const catSize = fitFontSize(ctx, category.trim(), CANVAS_SIZE - 160, 30, 18, 700);
      ctx.font = `700 ${catSize}px Arial, sans-serif`;
      ctx.fillStyle = '#ffd702';
      ctx.fillText(category.trim(), cx, 882);
    }

    // Logo, centrado abajo.
    if (logoImgRef.current) {
      const logo = logoImgRef.current;
      const logoW = 260;
      const logoH = (logo.height / logo.width) * logoW;
      ctx.drawImage(logo, cx - logoW / 2, CANVAS_SIZE - logoH - 56, logoW, logoH);
    }
  }, [name, category, pillText, uploadHint, photoKind]);

  useEffect(() => {
    draw();
  }, [draw, logoReady, photoVersion]);

  // Cada finalista tiene su propio "número de secuencia": si el usuario
  // cambia de selección varias veces seguidas, solo la imagen que termine
  // de cargar en último lugar (la del preset todavía seleccionado) puede
  // escribir en photoImgRef. Sin esto, una carga anterior que resuelve
  // tarde podía "adelantar" y pisar la foto correcta.
  const loadSeq = useRef(0);

  const applyPreset = (presetId: string) => {
    setSelectedPresetId(presetId);
    const seq = ++loadSeq.current;
    const preset = presets?.find((p) => p.id === presetId);
    if (!preset) {
      setName('');
      setCategory('');
      photoImgRef.current = null;
      setHasPhoto(false);
      setPhotoVersion((v) => v + 1);
      return;
    }
    setName(preset.name);
    setCategory(preset.category);
    setError('');
    setPhotoKind(preset.photoKind || 'photo');
    if (preset.photoUrl) {
      const img = new Image();
      img.onload = () => {
        if (seq !== loadSeq.current) return;
        photoImgRef.current = img;
        setHasPhoto(true);
        setPhotoVersion((v) => v + 1);
      };
      img.onerror = () => {
        if (seq !== loadSeq.current) return;
        photoImgRef.current = null;
        setHasPhoto(false);
        setPhotoVersion((v) => v + 1);
      };
      img.src = preset.photoUrl;
    } else {
      photoImgRef.current = null;
      setHasPhoto(false);
      setPhotoVersion((v) => v + 1);
    }
  };

  const handleFile = (file: File) => {
    if (!file.type.startsWith('image/')) {
      setError('Elige un archivo de imagen (JPG, PNG…).');
      return;
    }
    setError('');
    setPhotoKind('photo');
    const seq = ++loadSeq.current;
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        if (seq !== loadSeq.current) return;
        photoImgRef.current = img;
        setHasPhoto(true);
        setPhotoVersion((v) => v + 1);
      };
      img.src = reader.result as string;
    };
    reader.readAsDataURL(file);
  };

  const handleDownload = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const link = document.createElement('a');
    const safeName = name.trim() ? name.trim().toLowerCase().replace(/[^a-z0-9]+/gi, '-') : fileSuffix;
    link.download = `${safeName}-premios-novagob-2026-${fileSuffix}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  const inputId = `finalist-photo-${fileSuffix}`;

  const hasPresets = !!presets && presets.length > 0;
  const selectedPreset = presets?.find((p) => p.id === selectedPresetId);
  const showManualFields = editing || !hasPresets;

  return (
    <div className="finalist-frame-layout">
      <div className="finalist-frame-controls">
        {hasPresets && (
          <div className="form-group">
            <label className="form-label" htmlFor={`${inputId}-preset`}>
              Selecciona finalista
            </label>
            <select
              id={`${inputId}-preset`}
              className="form-input"
              value={selectedPresetId}
              onChange={(e) => {
                applyPreset(e.target.value);
                setEditing(false);
              }}
            >
              <option value="">Elige tu nombre…</option>
              {presets!.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.label}
                </option>
              ))}
            </select>
          </div>
        )}

        {hasPresets && selectedPreset && !editing && (
          <button
            type="button"
            className="finalist-edit-toggle"
            onClick={() => setEditing(true)}
          >
            <Pencil size={14} style={{ marginRight: 6 }} />
            No me convence, editar foto o datos
          </button>
        )}

        {hasPresets && editing && (
          <button
            type="button"
            className="finalist-edit-toggle"
            onClick={() => setEditing(false)}
          >
            <Check size={14} style={{ marginRight: 6 }} />
            Listo, ocultar edición
          </button>
        )}

        {showManualFields && (
          <>
            <div className="form-group">
              <label className="form-label" htmlFor={inputId}>
                Tu foto
              </label>
              <label htmlFor={inputId} className="finalist-upload-btn">
                <Upload size={16} style={{ marginRight: 8 }} />
                {hasPhoto ? 'Cambiar foto' : 'Subir foto'}
              </label>
              <input
                id={inputId}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleFile(file);
                }}
              />
              {error && <p style={{ color: '#ff6b6b', fontSize: 13, marginTop: 6 }}>{error}</p>}
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor={`${inputId}-name`}>
                Tu nombre
              </label>
              <input
                id={`${inputId}-name`}
                type="text"
                className="form-input"
                value={name}
                maxLength={60}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ej. Ana García"
              />
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor={`${inputId}-category`}>
                Categoría (opcional)
              </label>
              <select
                id={`${inputId}-category`}
                className="form-input"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
              >
                <option value="">Sin categoría</option>
                {categories.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>
          </>
        )}

        <button
          type="button"
          className="btn btn-primary w-full"
          onClick={handleDownload}
          disabled={!hasPhoto}
        >
          <Download size={16} style={{ marginRight: 8 }} />
          Descargar imagen
        </button>
        {!hasPhoto && (
          <p style={{ color: 'var(--color-text-secondary)', fontSize: 12.5, marginTop: 8, textAlign: 'center' }}>
            {hasPresets ? 'Elige tu nombre en la lista para generar tu imagen.' : 'Sube una foto para poder descargar tu imagen.'}
          </p>
        )}
      </div>

      <div className="finalist-frame-preview">
        <canvas ref={canvasRef} width={CANVAS_SIZE} height={CANVAS_SIZE} className="finalist-canvas" />
      </div>
    </div>
  );
};

export default FinalistShareCard;
